import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Search, 
  ShoppingCart,
  Clock,
  CheckCircle,
  Eye,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  FileText,
  DollarSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn, formatDate, formatCurrency } from '@/lib/utils';
import api from '@/services/api';
import { PermissionGate } from '@/components/PermissionGate';
import { useToast } from '@/hooks/useToast';

interface PurchaseOrder {
  id: string;
  poNumber: string;
  title: string;
  vendor?: { id: string; name: string };
  status: string;
  priority: string;
  totalAmount?: string;
  requestedBy?: { id: string; firstName: string; lastName: string };
  approvedBy?: { id: string; firstName: string; lastName: string };
  requestedDate: string;
  approvalDate?: string;
  expectedDeliveryDate?: string;
  items: PurchaseOrderItem[];
}

interface PurchaseOrderItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: string;
  totalPrice: string;
}

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  PENDING_APPROVAL: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  APPROVED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  ORDERED: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  RECEIVED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  CANCELLED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

const priorityColors: Record<string, string> = {
  LOW: 'text-gray-600',
  MEDIUM: 'text-blue-600',
  HIGH: 'text-orange-600',
  URGENT: 'text-red-600',
};

export default function PurchaseOrdersPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list');

  const { data, isLoading } = useQuery({
    queryKey: ['purchase-orders', { page, search, status }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      if (status) params.append('status', status);
      const response = await api.get(`/purchase-orders?${params.toString()}`);
      return response.data;
    },
  });

  const purchaseOrders: PurchaseOrder[] = data?.data || [];
  const pagination = data?.pagination;
  const stats = data?.stats;

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/purchase-orders/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      toast({ title: 'Purchase order deleted' });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      await api.patch(`/purchase-orders/${id}/status`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      toast({ title: 'Status updated' });
    },
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Purchase Orders</h1>
          <p className="text-muted-foreground">Track procurement requests and orders</p>
        </div>
        <PermissionGate permission="purchase_orders.create">
          <Button onClick={() => { setSelectedPO(null); setShowModal(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            New Purchase Order
          </Button>
        </PermissionGate>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <ShoppingCart className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold">{stats?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
                <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{stats?.pending || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Approved</p>
                <p className="text-2xl font-bold">{stats?.approved || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <FileText className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ordered</p>
                <p className="text-2xl font-bold">{stats?.ordered || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                <DollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Value</p>
                <p className="text-xl font-bold">{formatCurrency(stats?.totalValue || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by PO number, title, or vendor..."
                className="pl-10"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <select
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="PENDING_APPROVAL">Pending Approval</option>
              <option value="APPROVED">Approved</option>
              <option value="ORDERED">Ordered</option>
              <option value="RECEIVED">Received</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Purchase Orders Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full spinner" />
            </div>
          ) : purchaseOrders.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No purchase orders found</p>
              <Button variant="link" onClick={() => setShowModal(true)}>
                Create your first purchase order
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">PO Number</th>
                    <th className="text-left p-4 font-medium">Title</th>
                    <th className="text-left p-4 font-medium">Vendor</th>
                    <th className="text-left p-4 font-medium">Amount</th>
                    <th className="text-left p-4 font-medium">Priority</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Date</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {purchaseOrders.map((po) => (
                    <tr key={po.id} className="hover:bg-muted/50">
                      <td className="p-4">
                        <span className="font-mono font-medium text-primary">{po.poNumber}</span>
                      </td>
                      <td className="p-4">
                        <p className="font-medium">{po.title}</p>
                        <p className="text-sm text-muted-foreground">
                          {po.items?.length || 0} item(s)
                        </p>
                      </td>
                      <td className="p-4">{po.vendor?.name || '-'}</td>
                      <td className="p-4 font-medium">
                        {po.totalAmount ? formatCurrency(parseFloat(po.totalAmount)) : '-'}
                      </td>
                      <td className="p-4">
                        <span className={cn("font-medium", priorityColors[po.priority])}>
                          {po.priority}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={cn("px-2 py-1 text-xs font-medium rounded-full", statusColors[po.status])}>
                          {po.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="p-4 text-sm">{formatDate(po.requestedDate)}</td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => { setSelectedPO(po); setViewMode('detail'); }}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <PermissionGate permission="purchase_orders.update">
                            {(po.status === 'DRAFT' || po.status === 'PENDING_APPROVAL') && (
                              <Button variant="ghost" size="icon" onClick={() => { setSelectedPO(po); setShowModal(true); }}>
                                <Edit className="w-4 h-4" />
                              </Button>
                            )}
                          </PermissionGate>
                          <PermissionGate permission="purchase_orders.delete">
                            {po.status === 'DRAFT' && (
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => {
                                  if (confirm('Delete this purchase order?')) {
                                    deleteMutation.mutate(po.id);
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">Page {page} of {pagination.totalPages}</p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" disabled={page === 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" disabled={page === pagination.totalPages} onClick={() => setPage(page + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Add/Edit Modal */}
      {showModal && (
        <PurchaseOrderModal
          purchaseOrder={selectedPO}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
          }}
        />
      )}

      {/* Detail View */}
      {viewMode === 'detail' && selectedPO && (
        <PurchaseOrderDetail
          purchaseOrder={selectedPO}
          onClose={() => setViewMode('list')}
          onStatusChange={(status) => updateStatusMutation.mutate({ id: selectedPO.id, status })}
        />
      )}
    </div>
  );
}

interface PurchaseOrderModalProps {
  purchaseOrder: PurchaseOrder | null;
  onClose: () => void;
  onSuccess: () => void;
}

function PurchaseOrderModal({ purchaseOrder, onClose, onSuccess }: PurchaseOrderModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: purchaseOrder?.title || '',
    vendorId: purchaseOrder?.vendor?.id || '',
    priority: purchaseOrder?.priority || 'MEDIUM',
    expectedDeliveryDate: purchaseOrder?.expectedDeliveryDate?.split('T')[0] || '',
    notes: '',
    items: purchaseOrder?.items || [{ description: '', quantity: 1, unitPrice: '' }],
  });

  const { data: vendors } = useQuery({
    queryKey: ['vendors'],
    queryFn: async () => {
      const response = await api.get('/vendors');
      return response.data.data || [];
    },
  });

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unitPrice: '' }],
    });
  };

  const removeItem = (index: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const updateItem = (index: number, field: string, value: any) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (purchaseOrder) {
        await api.put(`/purchase-orders/${purchaseOrder.id}`, formData);
        toast({ title: 'Purchase order updated' });
      } else {
        await api.post('/purchase-orders', formData);
        toast({ title: 'Purchase order created' });
      }
      onSuccess();
    } catch (error) {
      toast({ title: 'Failed to save purchase order', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = formData.items.reduce((sum, item) => {
    return sum + (parseFloat(item.unitPrice as string) || 0) * (item.quantity || 0);
  }, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>{purchaseOrder ? 'Edit Purchase Order' : 'New Purchase Order'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium">Title *</label>
                <Input
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Office Computer Equipment"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Vendor</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={formData.vendorId}
                  onChange={(e) => setFormData({ ...formData, vendorId: e.target.value })}
                >
                  <option value="">Select Vendor</option>
                  {(vendors || []).map((v: any) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Priority</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="URGENT">Urgent</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Expected Delivery Date</label>
                <Input
                  type="date"
                  value={formData.expectedDeliveryDate}
                  onChange={(e) => setFormData({ ...formData, expectedDeliveryDate: e.target.value })}
                />
              </div>
            </div>

            {/* Line Items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Items</label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="w-4 h-4 mr-1" /> Add Item
                </Button>
              </div>
              {formData.items.map((item: any, index: number) => (
                <div key={index} className="flex gap-2 items-start">
                  <Input
                    placeholder="Description"
                    className="flex-1"
                    value={item.description}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Qty"
                    className="w-20"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                  />
                  <Input
                    type="number"
                    placeholder="Unit Price"
                    className="w-28"
                    step="0.01"
                    value={item.unitPrice}
                    onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                  />
                  {formData.items.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeItem(index)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <div className="text-right font-medium">
                Total: {formatCurrency(totalAmount)}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : (purchaseOrder ? 'Update' : 'Create')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

interface PurchaseOrderDetailProps {
  purchaseOrder: PurchaseOrder;
  onClose: () => void;
  onStatusChange: (status: string) => void;
}

function PurchaseOrderDetail({ purchaseOrder, onClose, onStatusChange }: PurchaseOrderDetailProps) {
  const nextStatus: Record<string, { label: string; value: string }> = {
    DRAFT: { label: 'Submit for Approval', value: 'PENDING_APPROVAL' },
    PENDING_APPROVAL: { label: 'Approve', value: 'APPROVED' },
    APPROVED: { label: 'Mark as Ordered', value: 'ORDERED' },
    ORDERED: { label: 'Mark as Received', value: 'RECEIVED' },
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{purchaseOrder.title}</CardTitle>
            <p className="text-sm text-muted-foreground font-mono">{purchaseOrder.poNumber}</p>
          </div>
          <span className={cn("px-3 py-1 text-sm font-medium rounded-full", statusColors[purchaseOrder.status])}>
            {purchaseOrder.status.replace('_', ' ')}
          </span>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Vendor</p>
              <p className="font-medium">{purchaseOrder.vendor?.name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Priority</p>
              <p className={cn("font-medium", priorityColors[purchaseOrder.priority])}>
                {purchaseOrder.priority}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Requested By</p>
              <p className="font-medium">
                {purchaseOrder.requestedBy 
                  ? `${purchaseOrder.requestedBy.firstName} ${purchaseOrder.requestedBy.lastName}` 
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Requested Date</p>
              <p className="font-medium">{formatDate(purchaseOrder.requestedDate)}</p>
            </div>
          </div>

          {/* Items Table */}
          <div>
            <h4 className="font-medium mb-2">Items</h4>
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-2">Description</th>
                  <th className="text-right p-2">Qty</th>
                  <th className="text-right p-2">Unit Price</th>
                  <th className="text-right p-2">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {purchaseOrder.items?.map((item) => (
                  <tr key={item.id}>
                    <td className="p-2">{item.description}</td>
                    <td className="text-right p-2">{item.quantity}</td>
                    <td className="text-right p-2">{formatCurrency(parseFloat(item.unitPrice))}</td>
                    <td className="text-right p-2">{formatCurrency(parseFloat(item.totalPrice))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2">
                <tr>
                  <td colSpan={3} className="p-2 text-right font-medium">Total</td>
                  <td className="p-2 text-right font-bold">
                    {formatCurrency(parseFloat(purchaseOrder.totalAmount || '0'))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex justify-between pt-4">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <div className="flex gap-2">
              {purchaseOrder.status !== 'CANCELLED' && purchaseOrder.status !== 'RECEIVED' && (
                <Button variant="destructive" onClick={() => onStatusChange('CANCELLED')}>
                  Cancel PO
                </Button>
              )}
              {nextStatus[purchaseOrder.status] && (
                <Button onClick={() => onStatusChange(nextStatus[purchaseOrder.status].value)}>
                  {nextStatus[purchaseOrder.status].label}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
